import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Eye, MessageSquareText, Pencil, Star, StarHalf, Trash2, TriangleAlert } from 'lucide-react';
import { reviewsAPI, productsAPI } from '../services/api';
import { Drawer, Modal, Popconfirm, Table, toast, Form, Field, useForm } from '../../../ui';
import { PageHeader, StatCard, FilterBar, LoadingState, ErrorState } from '../components';
import EmptyState from '../../../components/site/EmptyState';
import { formatDate, formatDateTime } from '../../../utils/format';
import { getErrorMessage } from '../../../utils/apiError';

const MAX_COMMENT = 2000;
const RATING_STEPS = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1];

// "4.5" for 4.5, "4" for 4.0
const formatRating = rating => {
  const value = Number(rating);
  if (!Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const productPath = (id, name) => `/products/${id}/${encodeURIComponent(name || 'project')}`;

const RatingValue = ({ rating }) => (
  <span className="adm-num" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
    <Star aria-hidden="true" width={14} height={14} fill="currentColor" />
    {formatRating(rating)}
    <span className="sr-only"> out of 5</span>
  </span>
);

const VerifiedBadge = ({ verified }) =>
  verified ? (
    <span className="pk-badge" data-tone="success">
      <BadgeCheck aria-hidden="true" />
      Verified purchase
    </span>
  ) : (
    <span className="pk-badge">Not verified</span>
  );

/** Edit rating and comment: PUT /Review/{id} { rating, comment }. */
const ReviewEditForm = ({ review, onCancel, onSaved }) => {
  const form = useForm();
  const [saving, setSaving] = useState(false);
  const initialValues = useMemo(
    () => ({
      // Snap odd values (e.g. 4.3) to the nearest half star the select offers.
      rating: String(Math.min(5, Math.max(1, Math.round((Number(review.rating) || 5) * 2) / 2))),
      comment: review.comment || '',
    }),
    [review]
  );

  const handleFinish = async values => {
    setSaving(true);
    try {
      const response = await reviewsAPI.update(review.id, {
        rating: Number(values.rating),
        comment: values.comment.trim(),
      });
      toast.success('Review updated');
      onSaved(response.data?.data || { ...review, rating: Number(values.rating), comment: values.comment.trim() });
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't update the review."));
    } finally {
      setSaving(false);
    }
  };

  const length = (form.getFieldValue('comment') || '').length;

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <p className="adm-note" style={{ marginTop: 0 }}>
        By {review.userName || 'a customer'}. Edit only to fix typos or remove personal details; keep the
        customer's opinion as written.
      </p>
      <Field name="rating" label="Rating" rules={[{ required: true, message: 'Choose a rating' }]}>
        <select className="pk-select" style={{ maxWidth: 200 }}>
          {RATING_STEPS.map(step => (
            <option key={step} value={String(step)}>
              {formatRating(step)} / 5
            </option>
          ))}
        </select>
      </Field>
      <Field
        name="comment"
        label="Comment"
        rules={[
          { required: true, whitespace: true, message: 'The comment can’t be empty' },
          { min: 3, message: 'Use at least 3 characters' },
          { max: MAX_COMMENT, message: `Keep it under ${MAX_COMMENT} characters` },
        ]}
        help={`${length}/${MAX_COMMENT}`}
      >
        <textarea className="pk-textarea" rows={6} maxLength={MAX_COMMENT} />
      </Field>
      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={saving} aria-busy={saving || undefined}>
          Save changes
        </button>
      </div>
    </Form>
  );
};

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [productNames, setProductNames] = useState({});
  const [productsFailed, setProductsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [reviewResult, productResult] = await Promise.allSettled([
      reviewsAPI.getAll(),
      productsAPI.getAllForAdmin(),
    ]);

    if (reviewResult.status === 'fulfilled') {
      const list = reviewResult.value.data?.data;
      setReviews(Array.isArray(list) ? list : []);
    } else if (reviewResult.reason?.response?.status === 404) {
      setReviews([]);
    } else {
      setError(getErrorMessage(reviewResult.reason, "Couldn't load reviews."));
    }

    if (productResult.status === 'fulfilled') {
      setProductNames(Object.fromEntries(productResult.value.map(p => [p.id, p.name])));
      setProductsFailed(false);
    } else {
      setProductsFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const productName = useCallback(id => productNames[id] || null, [productNames]);

  const handleDelete = async review => {
    try {
      await reviewsAPI.delete(review.id);
      setReviews(list => list.filter(r => r.id !== review.id));
      if (viewing?.id === review.id) setViewing(null);
      toast.success('Review deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't delete the review."));
    }
  };

  const handleSaved = updated => {
    setReviews(list => list.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
    if (viewing?.id === updated.id) setViewing(v => ({ ...v, ...updated }));
    setEditing(null);
  };

  const reviewedProducts = useMemo(() => {
    const ids = [...new Set(reviews.map(r => r.productId).filter(Boolean))];
    return ids
      .map(id => ({ id, name: productName(id) || `Product ${id.slice(-6)}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [reviews, productName]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reviews.filter(r => {
      const rating = Number(r.rating) || 0;
      if (ratingFilter && Math.floor(rating) !== Number(ratingFilter)) return false;
      if (verifiedFilter === 'yes' && !r.isVerifiedPurchase) return false;
      if (verifiedFilter === 'no' && r.isVerifiedPurchase) return false;
      if (productFilter && r.productId !== productFilter) return false;
      if (!term) return true;
      return [r.userName, r.comment, productName(r.productId)].some(v =>
        String(v || '').toLowerCase().includes(term)
      );
    });
  }, [reviews, search, ratingFilter, verifiedFilter, productFilter, productName]);

  const stats = useMemo(() => {
    const total = reviews.length;
    const sum = reviews.reduce((acc, r) => acc + (Number(r.rating) || 0), 0);
    const verified = reviews.filter(r => r.isVerifiedPurchase).length;
    return {
      total,
      average: total ? (sum / total).toFixed(1) : '—',
      verified,
      verifiedShare: total ? `${Math.round((verified / total) * 100)}% of reviews` : undefined,
      low: reviews.filter(r => Number(r.rating) < 3).length,
    };
  }, [reviews]);

  const renderProduct = id => {
    const name = productName(id);
    if (!name) return <span className="pk-muted">{productsFailed ? 'Unknown product' : 'Deleted product'}</span>;
    return (
      <Link to={productPath(id, name)} target="_blank" rel="noopener noreferrer" className="pk-link">
        {name}
      </Link>
    );
  };

  const columns = [
    {
      title: 'Review',
      key: 'review',
      render: (_, record) => (
        <div className="adm-cell__text">
          <RatingValue rating={record.rating} />
          <div className="adm-cell-sub adm-clamp-2" style={{ color: '#334155', fontSize: '0.8125rem' }}>
            {record.comment}
          </div>
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'productId',
      key: 'product',
      width: 220,
      render: id => renderProduct(id),
    },
    {
      title: 'Customer',
      dataIndex: 'userName',
      key: 'userName',
      width: 160,
      sorter: (a, b) => (a.userName || '').localeCompare(b.userName || ''),
      render: name => name || <span className="pk-muted">—</span>,
    },
    {
      title: 'Purchase',
      dataIndex: 'isVerifiedPurchase',
      key: 'verified',
      width: 170,
      render: verified => <VerifiedBadge verified={verified} />,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      render: date => <span className="adm-nowrap">{formatDate(date)}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <div className="adm-actions">
          <button type="button" className="adm-icon-btn" aria-label="View review" title="View" onClick={() => setViewing(record)}>
            <Eye aria-hidden="true" />
          </button>
          <button type="button" className="adm-icon-btn" aria-label="Edit review" title="Edit" onClick={() => setEditing(record)}>
            <Pencil aria-hidden="true" />
          </button>
          <Popconfirm
            title="Delete this review?"
            description="It's removed from the product page for good."
            okText="Delete"
            okDanger
            onConfirm={() => handleDelete(record)}
          >
            <button type="button" className="adm-icon-btn" data-tone="danger" aria-label="Delete review" title="Delete">
              <Trash2 aria-hidden="true" />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const hasFilters = Boolean(search || ratingFilter || verifiedFilter || productFilter);

  let body;
  if (loading) {
    body = <LoadingState label="Loading reviews" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load reviews" message={error} onRetry={load} />
      </div>
    );
  } else if (reviews.length === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState
          icon={<MessageSquareText />}
          title="No reviews yet"
          text="Customers can review a project after buying it. Their reviews will show up here."
        />
      </div>
    );
  } else {
    body = (
      <>
        <FilterBar
          inline
          searchLabel="Search reviews"
          searchPlaceholder="Search customer, product or comment"
          searchValue={search}
          onSearchChange={setSearch}
          filters={[
            {
              key: 'rating',
              component: (
                <select className="pk-select" aria-label="Filter by rating" value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}>
                  <option value="">Any rating</option>
                  <option value="5">5 stars</option>
                  <option value="4">4 to 4.5 stars</option>
                  <option value="3">3 to 3.5 stars</option>
                  <option value="2">2 to 2.5 stars</option>
                  <option value="1">1 to 1.5 stars</option>
                </select>
              ),
            },
            {
              key: 'verified',
              component: (
                <select className="pk-select" aria-label="Filter by purchase" value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value)}>
                  <option value="">Verified and not</option>
                  <option value="yes">Verified purchase</option>
                  <option value="no">Not verified</option>
                </select>
              ),
            },
            {
              key: 'product',
              component: (
                <select className="pk-select" aria-label="Filter by product" value={productFilter} onChange={e => setProductFilter(e.target.value)}>
                  <option value="">All products</option>
                  {reviewedProducts.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
          onClearFilters={
            hasFilters
              ? () => {
                  setSearch('');
                  setRatingFilter('');
                  setVerifiedFilter('');
                  setProductFilter('');
                }
              : undefined
          }
          end={<span className="adm-toolbar__count">{filtered.length} shown</span>}
        />
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          scroll={{ x: 980 }}
          emptyText="No reviews match these filters."
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title="Reviews"
        description="What customers wrote about their purchases. You can fix a review or delete it; new reviews come only from buyers."
      />

      {!error && (
        <div className="adm-stats">
          <StatCard title="Reviews" value={stats.total} icon={<MessageSquareText />} loading={loading} />
          <StatCard title="Average rating" value={stats.average} suffix={stats.total ? ' / 5' : ''} icon={<StarHalf />} loading={loading} />
          <StatCard title="Verified purchases" value={stats.verified} hint={stats.verifiedShare} icon={<BadgeCheck />} loading={loading} />
          <StatCard title="Below 3 stars" value={stats.low} icon={<TriangleAlert />} tone={stats.low ? 'warning' : undefined} loading={loading} />
        </div>
      )}

      {productsFailed && !loading && !error && (
        <div className="pk-alert" data-tone="warning" role="status" style={{ marginBottom: 16 }}>
          <TriangleAlert aria-hidden="true" />
          <span>
            Product names couldn't be loaded, so some rows say "Unknown product".{' '}
            <button type="button" className="pk-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' }} onClick={load}>
              Try again
            </button>
          </span>
        </div>
      )}

      <section className="adm-panel" aria-label="Reviews">
        {body}
      </section>

      <Drawer
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title="Review"
        width={460}
        className="adm-drawer"
        footer={
          viewing && (
            <div className="pk-cluster" style={{ justifyContent: 'flex-end', padding: '4px 8px' }}>
              <button
                type="button"
                className="pk-btn pk-btn--secondary pk-btn--sm"
                onClick={() => {
                  setEditing(viewing);
                }}
              >
                <Pencil aria-hidden="true" />
                Edit
              </button>
            </div>
          )
        }
      >
        {viewing && (
          <>
            <div className="adm-detail">
              <RatingValue rating={viewing.rating} />
            </div>
            <div className="adm-detail">
              <p className="adm-message">{viewing.comment}</p>
            </div>
            <div className="adm-detail">
              <dl className="pk-rows">
                <div>
                  <dt>Product</dt>
                  <dd>{renderProduct(viewing.productId)}</dd>
                </div>
                <div>
                  <dt>Customer</dt>
                  <dd>{viewing.userName || '—'}</dd>
                </div>
                <div>
                  <dt>Purchase</dt>
                  <dd>
                    <VerifiedBadge verified={viewing.isVerifiedPurchase} />
                  </dd>
                </div>
                <div>
                  <dt>Written</dt>
                  <dd>{formatDateTime(viewing.createdAt)}</dd>
                </div>
                {viewing.updatedAt && viewing.updatedAt !== viewing.createdAt && (
                  <div>
                    <dt>Last edited</dt>
                    <dd>{formatDateTime(viewing.updatedAt)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </>
        )}
      </Drawer>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title="Edit review"
        footer={null}
        width={600}
        className="adm-modal"
        maskClosable={false}
      >
        {editing && <ReviewEditForm review={editing} onCancel={() => setEditing(null)} onSaved={handleSaved} />}
      </Modal>
    </div>
  );
};

export default ReviewManagement;
